/**
 * ScreenShareRequestBanner — UI cross-role do fluxo "compartilhar tela do
 * equipamento" (PRD 4, Story 4.5.2 + PRD § 2.8).
 *
 * Discriminated union por `role`:
 *
 * - **agent**: botão "🖥 Pedir compartilhamento de tela" no painel da
 *   chamada. Click → POST /atendimentos/{id}/screen-share-request →
 *   backend emite `call_data_event(type='screen_share_request')`.
 *   Botão tem cooldown de 30s após envio bem-sucedido pra anti-spam
 *   (o parceiro pode demorar pra clicar no modal).
 *
 * - **partner**: modal de aceite quando `incomingRequest !== null`.
 *   O parent observa `useCallDataEvents.events` (PRD 3 vai popular real;
 *   em PRD 0 fica `[]` e o parent injeta `incomingRequest` por outro
 *   caminho — typicamente WS dedicado ou polling). Click "Aceitar" →
 *   `navigator.mediaDevices.getDisplayMedia()` → `onAccept(stream)` →
 *   POST `/portal/atendimentos/{id}/screen-share-response` com
 *   `accepted=true`. Click "Recusar" → POST com `accepted=false`,
 *   **sem** chamar getDisplayMedia.
 *
 * O componente é puro: não toca em LiveKit Room. A publicação do track
 * é responsabilidade do `onAccept` injetado pelo parent (vai ser
 * `room.localParticipant.publishTrack(stream.getVideoTracks()[0])`
 * quando PRD 3 ligar o LiveKit client). Isso mantém testabilidade
 * (mock simples) e zero acoplamento com SDK que ainda não está em
 * `package.json`.
 *
 * Refs:
 * - Story 4.5.2: docs/MODULO_EXPANSAO_2026/stories/individual/4.5.2.md
 * - PRD § 2.8: docs/MODULO_EXPANSAO_2026/prds/PRD-4-atendimento-parceiro.md
 * - Backend agent endpoint: V1/backend/app/api/v1/endpoints/atendimentos.py
 * - Backend partner endpoint: V1/backend/app/api/v1/endpoints/portal_atendimentos.py
 */

import { useEffect, useRef, useState } from "react";
import { AlertCircle, Loader2, MonitorUp } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { atendimentosApi } from "@/services/atendimentosApi";
import { portalAtendimentosApi } from "@/services/portalAtendimentosApi";

/**
 * Cooldown em segundos depois de uma request bem-sucedida.
 *
 * Cobre o tempo típico de leitura + decisão do parceiro (~20s) com
 * folga. Não é trava no backend — apenas UX. Server-side, o
 * `CallDataEventService` audita cada request, então clicks repetidos
 * NÃO quebram nada (só poluem o painel do parceiro).
 */
const AGENT_REQUEST_COOLDOWN_SECONDS = 30;


export type ScreenShareRequestBannerProps =
  | {
      role: "agent";
      sessionId: string;
      /** Quando true, desabilita o botão (ex: chamada não iniciada). */
      disabled?: boolean;
      /** Callback opcional após request bem-sucedida. */
      onRequestSent?: (response: {
        event_id: string;
        message: string;
        requested_at: string;
      }) => void;
    }
  | {
      role: "partner";
      sessionId: string;
      /**
       * Request entrante observado pelo parent. `null` = nenhum modal.
       *
       * Em PRD 3 isso vem de `useCallDataEvents.events.find(e =>
       * e.eventType === "screen_share_request")`. Hoje (PRD 0) o parent
       * é responsável por popular — testes injetam direto.
       */
      incomingRequest: { eventId: string; message: string } | null;
      /**
       * Callback executado APÓS `getDisplayMedia()` resolver e ANTES
       * do POST de resposta. Use pra `room.localParticipant.publishTrack(...)`.
       * Se rejeitar, o modal mostra erro e mantém aberto.
       */
      onAccept: (stream: MediaStream) => Promise<void>;
      /** Chamado depois de aceite ou recusa bem-sucedidos. */
      onDismiss?: () => void;
    };


export function ScreenShareRequestBanner(props: ScreenShareRequestBannerProps) {
  if (props.role === "agent") {
    return <AgentSide {...props} />;
  }
  return <PartnerSide {...props} />;
}


// ---------------------------------------------------------------------------
// Agent side
// ---------------------------------------------------------------------------

function AgentSide({
  sessionId,
  disabled,
  onRequestSent,
}: Extract<ScreenShareRequestBannerProps, { role: "agent" }>) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  // Guarda o setInterval pra limpar no unmount — evita leak quando o
  // componente desmonta no meio do cooldown.
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const startCooldown = () => {
    setCooldownRemaining(AGENT_REQUEST_COOLDOWN_SECONDS);
    intervalRef.current = setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1) {
          if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleClick = async () => {
    setIsLoading(true);
    try {
      const response = await atendimentosApi.requestScreenShare(sessionId);
      onRequestSent?.({
        event_id: response.event_id,
        message: response.message,
        requested_at: response.requested_at,
      });
      startCooldown();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ScreenShareRequestBanner/agent] request failed", err);
      toast({
        variant: "destructive",
        title: "Falha ao enviar pedido",
        description:
          "Não foi possível pedir o compartilhamento de tela ao parceiro. Tente novamente.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = Boolean(disabled) || isLoading || cooldownRemaining > 0;
  const label =
    cooldownRemaining > 0
      ? `Aguardando resposta (${cooldownRemaining}s)`
      : isLoading
        ? "Enviando..."
        : "Pedir compartilhamento de tela";

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleClick}
      disabled={isDisabled}
      data-testid="screen-share-request-button"
      aria-busy={isLoading}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
      ) : (
        <MonitorUp className="h-4 w-4 mr-2" aria-hidden="true" />
      )}
      {label}
    </Button>
  );
}


// ---------------------------------------------------------------------------
// Partner side
// ---------------------------------------------------------------------------

function PartnerSide({
  sessionId,
  incomingRequest,
  onAccept,
  onDismiss,
}: Extract<ScreenShareRequestBannerProps, { role: "partner" }>) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Quando o request entrante muda (ex: novo request chega), limpa o
  // estado de erro/processando. Faz o modal "reset" entre requests sem
  // que o parent precise unmount/remount.
  useEffect(() => {
    setErrorMessage(null);
    setIsProcessing(false);
  }, [incomingRequest?.eventId]);

  if (incomingRequest === null) {
    return null;
  }

  const handleAccept = async () => {
    setErrorMessage(null);
    setIsProcessing(true);
    let stream: MediaStream | null = null;
    try {
      // getDisplayMedia requer user gesture — o click no botão satisfaz.
      // Sem video:true, alguns browsers (Firefox) recusam.
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[ScreenShareRequestBanner/partner] getDisplayMedia failed", err);
      setErrorMessage(
        "Não foi possível compartilhar a tela. Verifique permissões do navegador.",
      );
      setIsProcessing(false);
      return;
    }

    try {
      await onAccept(stream);
      await portalAtendimentosApi.respondToScreenShareRequest(sessionId, true);
      onDismiss?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ScreenShareRequestBanner/partner] publish/respond failed", err);
      // Para o stream para liberar a câmera/tela do usuário se falhamos
      // depois de já tê-lo capturado — evita o ícone do browser
      // permanecer ativo "compartilhando" sem que nada esteja sendo
      // publicado.
      stream.getTracks().forEach((t) => t.stop());
      // Mensagem neutra — não dá a entender que o compartilhamento está
      // ativo, já que paramos os tracks acima. Reclick re-abre o seletor
      // de tela do browser.
      setErrorMessage("Falha ao registrar compartilhamento. Tente novamente.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    setErrorMessage(null);
    setIsProcessing(true);
    try {
      await portalAtendimentosApi.respondToScreenShareRequest(sessionId, false);
      onDismiss?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ScreenShareRequestBanner/partner] decline failed", err);
      setErrorMessage("Falha ao recusar o pedido. Tente novamente.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <AlertDialog open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Pedido de compartilhamento de tela</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2 text-left">
            <span className="block">{incomingRequest.message}</span>
            <span className="block text-xs text-muted-foreground">
              Ao aceitar, sua tela será capturada e incluída na gravação da chamada
              (LGPD — mesma retenção da chamada principal).
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {errorMessage !== null && (
          <Alert variant="destructive" role="alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleDecline}
            disabled={isProcessing}
            data-testid="screen-share-decline-button"
          >
            Recusar
          </Button>
          <Button
            type="button"
            onClick={handleAccept}
            disabled={isProcessing}
            data-testid="screen-share-accept-button"
            autoFocus
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                Processando...
              </>
            ) : (
              "Aceitar e compartilhar"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
